/**
 * The browser's half of InsightsLinks (app/Services/BlinkPages/SiteAnalytics/Insights/InsightsLinks.php,
 * D45 of documentation/pr-plans/insights-s0-plan-and-seats.md): the names an address into the Insights
 * page carries, and what a link's address asks the page to open. PHP builds every such link; the page's
 * scripts read it here, so a link and the code that follows it in place never disagree on a name.
 */

/** InsightsView::QUERY_KEY and its Stats value; Actions has no parameter. */
export const VIEW_PARAM = "view";

export const STATS_VIEW = "stats";

/** InsightsView::Guides, and InsightsLinks::GUIDE and QUERY: the guide the Guides view shows, and its search (D181). */
export const GUIDES_VIEW = "guides";

export const GUIDE_PARAM = "guide";

export const QUERY_PARAM = "q";

/** The Guides view's own parameters, which leaving it leaves behind. */
export const GUIDES_LINK_PARAMS = [GUIDE_PARAM, QUERY_PARAM];

/** InsightsLinks::ACTIVITY, ROW, TOPIC and BACK. */
export const ACTIVITY_PARAM = "activity";

export const ROW_PARAM = "row";

export const TOPIC_PARAM = "topic";

export const BACK_PARAM = "back";

/**
 * The band What brings people here has open (D148): a search intent, Not sorted, Branded queries or Not relevant,
 * written by the card itself as a reader drills in (site-analytics-topics.js), beside ?topic= for an opened topic.
 * No link builds it: a link names a topic, which opens in the band its search intent puts it in.
 */
export const INTENT_PARAM = "intent";

/** InsightsLinks::topicInStats()'s parameters besides the view, and the card's band: Stats' own, so leaving Stats leaves them. */
export const STATS_LINK_PARAMS = [TOPIC_PARAM, BACK_PARAM, INTENT_PARAM];

/**
 * Stats' Period as its address names it (Sites\Analytics' #[Url] period, start and end; InsightsLinks::statsPeriods()).
 * Written by a See in Stats link from an activity, so Stats opens over the list's Period (D111, B7).
 */
export const PERIOD_PARAM = "period";

export const START_PARAM = "start";

export const END_PARAM = "end";

export const STATS_PERIOD_PARAMS = [PERIOD_PARAM, START_PARAM, END_PARAM];

/** SiteAnalyticsPeriod::Custom: a range, from start to end, both days included. */
export const CUSTOM_PERIOD = "custom";

/**
 * Other queries' group id (SiteTopicAssignment::OTHER), which ?topic= names as it names a topic, so its
 * See this topic in Stats draws it alone in What brings people here (Ola's QA, 9 Oct).
 */
export const OTHER_QUERIES_GROUP = 0;

/**
 * A group id as an address carries it: a topic's (site_topics.id), a whole number above 0, or Other
 * queries' 0; else null. The server still checks a topic is one of the site's
 * (TopicsCardBuilder::chosenTopics()), and the card focuses only a group its answer lists.
 */
export function topicId(value) {
    const text = String(value ?? "");

    return /^(0|[1-9][0-9]{0,9})$/.test(text) ? Number(text) : null;
}

/**
 * What a link into this Insights page asks for, or null for a link the page does not open in place:
 * one to another page or another site, or one that names nothing it knows.
 *
 * - `{view: "stats", topic, back}`: InsightsLinks::topicInStats(), a topic drawn on Stats, and the
 *   activity it came from, or null; InsightsLinks::stats() names no topic, and `topic` is null.
 * - `{view: "actions", activity, row}`: InsightsLinks::activity(), one of `activities` open on Actions,
 *   with the row to bring forward, or null.
 * - `{view: "guides", guide, section, query}`: InsightsLinks::guides(), the Guides view on its hub (guide null)
 *   or on one guide, at the section the fragment names, or null (D181).
 *
 * @param {URL} url the link's address, resolved
 * @param {URL} here the page's own address
 * @param {string[]} activities the six activities' keys (InsightsLinks::activityKeys())
 */
export function linkTarget(url, here, activities) {
    if (url.origin !== here.origin || url.pathname !== here.pathname) {
        return null;
    }

    const params = url.searchParams;
    const activityOf = (key) => (activities.includes(key) ? key : null);

    if (params.get(VIEW_PARAM) === GUIDES_VIEW) {
        return { view: GUIDES_VIEW, guide: params.get(GUIDE_PARAM) || null, section: url.hash.replace(/^#/, "") || null, query: params.get(QUERY_PARAM) || null };
    }

    if (params.get(VIEW_PARAM) === STATS_VIEW) {
        const topic = topicId(params.get(TOPIC_PARAM));

        // A topic the address names but no group can be is not a link this page drew.
        return topic === null && params.has(TOPIC_PARAM) ? null : { view: STATS_VIEW, topic, back: activityOf(params.get(BACK_PARAM)) };
    }

    const activity = activityOf(params.get(ACTIVITY_PARAM));

    return activity === null || params.has(VIEW_PARAM) ? null : { view: "actions", activity, row: params.get(ROW_PARAM) || null };
}

/**
 * Stats' Period for an activity's period (its window control's time_range, InsightsActivity::$timeRange), as
 * the address names it: `{period}` for one of Stats' presets, or `{period: "custom", start, end}` for a range,
 * both days included; null for anything else, which leaves Stats on its own Period.
 *
 * @param {unknown} timeRange a preset's value ("last_28_days"), or a range with its end excluded ("2026-01-01 : 2026-04-01")
 * @param {Record<string, {period: string, start?: string, end?: string}>} periods InsightsLinks::statsPeriods()
 */
export function statsPeriod(timeRange, periods = {}) {
    if (typeof timeRange !== "string") {
        return null;
    }

    if (Object.prototype.hasOwnProperty.call(periods, timeRange)) {
        return { ...periods[timeRange] };
    }

    const parts = timeRange.split(" : ");
    const day = /^\d{4}-\d{2}-\d{2}$/;

    if (parts.length !== 2 || !day.test(parts[0]) || !day.test(parts[1]) || parts[0] >= parts[1]) {
        return null;
    }

    // The control's end is the day after the last; Stats' is the last day itself.
    const last = new Date(`${parts[1]}T00:00:00Z`);

    last.setUTCDate(last.getUTCDate() - 1);

    return { period: CUSTOM_PERIOD, start: parts[0], end: last.toISOString().slice(0, 10) };
}
